/**
 * useCytoscapeInstance Hook
 *
 * Creates and manages Cytoscape.js instance with WebGL rendering.
 */

import type { Core, EdgeSingular, NodeSingular } from 'cytoscape'
import cytoscape from 'cytoscape'
import cola from 'cytoscape-cola'
import cxtmenu from 'cytoscape-cxtmenu'
import fcose from 'cytoscape-fcose'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isMobileViewport } from '@/hooks/use-mobile'
import { AuthedImageCache } from '@/lib/authed-image-cache'
import { api } from '@/services/api'
import { resolveAuthedNodeImages } from './authed-node-images'
import type { GraphTheme } from './entity-icons'
import { FAN_RADIUS, placeBesideAnchor } from './expand-placement'
import { fitGraph, fitPadding, phoneMinZoom } from './fit-padding'
import { addEdgesSafely, safeBatch } from './safe-batch'
import { createSpectreStylesheet } from './styles'
import { type GraphData, graphDataToCytoscapeElements } from './types'

// Track extension registration
let extensionsRegistered = false

/**
 * Register Cytoscape extensions. Safe to call multiple times.
 * Works in both SSR and pure React environments.
 */
const registerExtensions = async (): Promise<void> => {
  if (extensionsRegistered) return

  // Check for browser environment (works for both SSR check and pure React)
  if (typeof window === 'undefined') return

  try {
    // Register fcose layout
    if (typeof cytoscape('core', 'fcose') === 'undefined') {
      cytoscape.use(fcose)
    }

    // Register cola layout
    if (typeof cytoscape('core', 'cola') === 'undefined') {
      cytoscape.use(cola)
    }

    // Register lasso selection - dynamic import for SSR compatibility
    const cytoscapeLasso = (await import('cytoscape-lasso')).default
    if (typeof cytoscape('core', 'lassoSelectionEnabled') === 'undefined') {
      cytoscape.use(cytoscapeLasso)
    }

    // Register context menu
    if (typeof cytoscape('core', 'cxtmenu') === 'undefined') {
      cytoscape.use(cxtmenu)
    }

    extensionsRegistered = true
  } catch (e) {
    console.warn('Failed to register Cytoscape extensions:', e)
  }
}

// For pure React environments, register immediately if window exists
if (typeof window !== 'undefined') {
  registerExtensions()
}

/**
 * Post-layout safety net for Clusters view. After fcose settles, compute the
 * bounding box of every compound parent, lay out those bounding boxes on a
 * grid with fixed spacing, and translate each compound's children by the delta
 * between old and new compound centroid. Edges follow their endpoints
 * automatically. No-op when there are fewer than 2 compounds.
 */
function expandCompoundCentroids(cy: Core) {
  const compounds = cy.nodes(':parent')
  if (compounds.length < 2) return

  // Target centroids on a square grid
  const n = compounds.length
  const cols = Math.ceil(Math.sqrt(n))
  const cellW = 1400
  const cellH = 1000

  compounds.forEach((parent, idx) => {
    const row = Math.floor(idx / cols)
    const col = idx % cols
    const targetCx = col * cellW
    const targetCy = row * cellH

    const bb = parent.boundingBox({ includeLabels: false })
    const currentCx = (bb.x1 + bb.x2) / 2
    const currentCy = (bb.y1 + bb.y2) / 2
    const dx = targetCx - currentCx
    const dy = targetCy - currentCy

    parent.descendants().forEach(child => {
      const p = child.position()
      child.position({ x: p.x + dx, y: p.y + dy })
    })
  })
}

export interface UseCytoscapeInstanceOptions {
  container: HTMLElement | null
  data: GraphData
  layout?:
    | 'force'
    | 'hierarchical'
    | 'radial'
    | 'cose'
    | 'fcose'
    | 'cola'
    | 'clusters'
    | 'timeline'
    | 'grid'
    | 'circle'
    | 'breadthfirst'
    | 'concentric'
    | 'custom'
  onReady?: (cy: Core) => void
  caseId?: string // For saving/loading node positions
  theme?: GraphTheme
  // Use texture rendering during pan/zoom. Big perf win on large graphs
  // (explore-graph), but can leave parent bg visible around the edges on
  // small graphs. Off by default.
  textureOnViewport?: boolean
  // Free-form per-case UI state included in every saveLayout call. Consumers
  // own the shape (e.g. `{ layout: 'fcose' }`). Pass a fresh object whenever
  // the value changes — the hook refs it and `persist()` flushes it.
  uiState?: Record<string, unknown> | null
  // Fires once after the initial getLayout() resolves so consumers can seed
  // their local state (e.g. selected layout) from the saved ui_state.
  onUiStateLoaded?: (uiState: Record<string, unknown> | null) => void
}

export interface UseCytoscapeInstanceReturn {
  cy: Core | null
  isReady: boolean
  updateData: (data: GraphData) => void
  fitToView: () => void
  resize: () => void
  zoomIn: () => void
  zoomOut: () => void
  resetZoom: () => void
  panToNode: (nodeId: string) => void
  setLassoEnabled: (enabled: boolean) => void
  isLassoEnabled: boolean
  // Re-run the currently selected layout (toolbar "Tidy up" button).
  runLayout: () => void
  // Flush current positions + ui_state to the backend immediately, bypassing
  // the drag-end debounce. Use after a ui_state change (e.g. layout switch).
  persist: () => void
}

export function useCytoscapeInstance({
  container,
  data,
  layout = 'custom',
  onReady,
  caseId,
  theme = 'dark',
  textureOnViewport = false,
  uiState,
  onUiStateLoaded,
}: UseCytoscapeInstanceOptions): UseCytoscapeInstanceReturn {
  const cyRef = useRef<Core | null>(null)
  // One bounded blob cache per graph instance; released with the instance.
  const authedImagesRef = useRef<AuthedImageCache>(new AuthedImageCache())
  const [isReady, setIsReady] = useState(false)
  const dataRef = useRef(data)
  const layoutRef = useRef(layout)
  const onReadyRef = useRef(onReady)
  const uiStateRef = useRef(uiState)
  const savePositionsRef = useRef<() => void>(() => {})
  const onUiStateLoadedRef = useRef(onUiStateLoaded)
  onUiStateLoadedRef.current = onUiStateLoaded

  // Keep refs updated
  dataRef.current = data
  layoutRef.current = layout
  uiStateRef.current = uiState
  onReadyRef.current = onReady

  // Ensure extensions are registered on mount (for SSR environments)
  useEffect(() => {
    registerExtensions()
  }, [])

  // Initialize Cytoscape
  useEffect(() => {
    if (!container) return

    const initGraph = async () => {
      const elements = graphDataToCytoscapeElements(dataRef.current)

      // Load saved positions and ui_state from backend if available
      let savedPositions: Record<string, { x: number; y: number }> = {}
      let loadedUiState: Record<string, unknown> | null = null
      if (caseId) {
        try {
          const layoutData = await api.graph.getLayout(caseId)
          savedPositions = layoutData.node_positions || {}
          loadedUiState = layoutData.ui_state ?? null
        } catch (_err) {
          console.log('No saved layout found, will use default layout')
        }
      }

      // Apply saved positions to node elements
      elements.forEach(el => {
        if (el.group === 'nodes' && el.data.id && savedPositions[el.data.id]) {
          el.position = savedPositions[el.data.id]
        }
      })

      const cy = cytoscape({
        container,
        elements,
        style: createSpectreStylesheet(theme),

        // ═══════════════════════════════════════════════════════════════
        // LAYOUT - preset (saved positions) or fcose for new graphs
        // ═══════════════════════════════════════════════════════════════
        layout: {
          name: 'preset',
          fit: true,
          padding: fitPadding(50),
        },

        // ═══════════════════════════════════════════════════════════════
        // PERFORMANCE SETTINGS
        // ═══════════════════════════════════════════════════════════════
        hideEdgesOnViewport: false,
        hideLabelsOnViewport: false, // Keep labels visible
        textureOnViewport,
        motionBlur: false,
        pixelRatio: 'auto',
        wheelSensitivity: 0.3,
        minZoom: phoneMinZoom(0.1),
        maxZoom: 4,

        // ═══════════════════════════════════════════════════════════════
        // SELECTION - single select by default, box selection with lasso
        // ═══════════════════════════════════════════════════════════════
        selectionType: 'single', // Changed from 'additive' to fix multi-select issue
        boxSelectionEnabled: false, // Disable native box selection, using lasso instead

        // ═══════════════════════════════════════════════════════════════
        // INTERACTION
        // ═══════════════════════════════════════════════════════════════
        userPanningEnabled: true,
        userZoomingEnabled: true,
        autoungrabify: false,
      })

      cyRef.current = cy
      // The preset layout's own fit is symmetric; keep it clear of the phone toolbar.
      if (isMobileViewport()) fitGraph(cy, 50)
      // Node crops under /api/ need the bearer token — fetch them into the
      // blob cache and write the resulting URLs back onto the nodes.
      void resolveAuthedNodeImages(cy, authedImagesRef.current)
      setIsReady(true)
      onReadyRef.current?.(cy)
      onUiStateLoadedRef.current?.(loadedUiState)

      // Expose to window for debugging (development only)
      if (process.env.NODE_ENV === 'development') {
        ;(window as any).cytoscapeInstance = cy
        console.log('[Cytoscape] Instance exposed to window.cytoscapeInstance for debugging')
      }

      // ═══════════════════════════════════════════════════════════════
      // SAVE POSITIONS TO BACKEND - Debounced on drag end
      // ═══════════════════════════════════════════════════════════════
      let savePositionsTimeout: NodeJS.Timeout | null = null

      const savePositionsToBackend = () => {
        if (!caseId) return

        const positions: Record<string, { x: number; y: number }> = {}
        cy.nodes().forEach(node => {
          const pos = node.position()
          positions[node.id()] = { x: pos.x, y: pos.y }
        })

        api.graph
          .saveLayout(caseId, {
            node_positions: positions,
            expanded_nodes: [], // TODO: track expanded nodes if needed
            ui_state: uiStateRef.current ?? null,
          })
          .catch(err => {
            console.error('Failed to save graph layout:', err)
          })
      }
      savePositionsRef.current = savePositionsToBackend

      // ═══════════════════════════════════════════════════════════════
      // DRAG SIMULATION - Connected nodes follow the dragged node
      // Disabled: caused unwanted cascade when moving a single node.
      // Kept commented for potential revisit as an opt-in "sticky drag" mode.
      // ═══════════════════════════════════════════════════════════════
      // let dragStartPosition: { x: number; y: number } | null = null
      // let connectedNodes: cytoscape.NodeCollection | null = null
      // const originalPositions: Map<string, { x: number; y: number }> = new Map()
      //
      // cy.on('grab', 'node', event => {
      //   const node = event.target as NodeSingular
      //   dragStartPosition = { ...node.position() }
      //
      //   // Get directly connected nodes (neighbors)
      //   connectedNodes = node.neighborhood('node')
      //
      //   // Store original positions
      //   originalPositions.clear()
      //   connectedNodes.forEach((n: NodeSingular) => {
      //     originalPositions.set(n.id(), { ...n.position() })
      //   })
      // })
      //
      // cy.on('drag', 'node', event => {
      //   if (!dragStartPosition || !connectedNodes) return
      //
      //   const node = event.target as NodeSingular
      //   const currentPosition = node.position()
      //
      //   // Calculate delta from drag start
      //   const dx = currentPosition.x - dragStartPosition.x
      //   const dy = currentPosition.y - dragStartPosition.y
      //
      //   // Move connected nodes partially (follow factor: 0.3 = 30% of the movement)
      //   const followFactor = 0.3
      //
      //   connectedNodes.forEach((neighbor: NodeSingular) => {
      //     const original = originalPositions.get(neighbor.id())
      //     if (original) {
      //       neighbor.position({
      //         x: original.x + dx * followFactor,
      //         y: original.y + dy * followFactor,
      //       })
      //     }
      //   })
      // })

      // 'dragfree' (not 'free'): only a real drag moves a node. A plain
      // click/tap also fires 'free' and would overwrite the saved layout.
      cy.on('dragfree', 'node', () => {
        // Save positions to backend (debounced)
        if (savePositionsTimeout) {
          clearTimeout(savePositionsTimeout)
        }
        savePositionsTimeout = setTimeout(() => {
          savePositionsToBackend()
        }, 1000) // Wait 1 second after user stops dragging
      })

      // If no positions, run layout
      const hasPositions = elements.some(el => el.group === 'nodes' && el.position)
      if (!hasPositions && elements.length > 0) {
        if (layoutRef.current === 'timeline') {
          // Timeline layout: arrange Event nodes chronologically, keep others visible
          const eventNodes = cy.nodes('[typeLabel = "Event"]')
          const otherNodes = cy.nodes('[typeLabel != "Event"]')

          if (eventNodes.length > 0) {
            // Extract dates from Event nodes
            const nodesWithDates = eventNodes
              .map(node => {
                const props = node.data('properties') || {}
                const dateStr =
                  props.date || props.created_at || props.timestamp || props.event_date
                const date = dateStr ? new Date(dateStr) : null
                return { node, date, dateStr }
              })
              .sort((a, b) => {
                if (!a.date || !b.date) return 0
                return a.date.getTime() - b.date.getTime()
              })

            // Calculate timeline positions
            const graphWidth = cy.width()
            const _graphHeight = cy.height()
            const timelineY = 200 // Fixed Y position for timeline
            const spacing = Math.max(200, graphWidth / (nodesWithDates.length || 1))

            // First layout non-event nodes with hierarchical tree layout
            if (otherNodes.length > 0) {
              otherNodes
                .layout({
                  name: 'breadthfirst',
                  animate: false,
                  fit: false,
                  directed: true,
                  spacingFactor: 0.5,
                  avoidOverlap: true,
                  nodeDimensionsIncludeLabels: false,
                } as any)
                .run()
            }

            // Then position Event nodes on timeline
            nodesWithDates.forEach((item, index) => {
              item.node.position({
                x: 100 + index * spacing,
                y: timelineY,
              })
            })

            fitGraph(cy, 80)
          }
        } else {
          // Use fcose (force-directed Compound Spring Embedder) layout
          const fcoseLayout = {
            name: 'fcose',
            animate: true,
            animationDuration: 1200,
            animationEasing: 'ease-out',
            fit: true,
            padding: fitPadding(120),

            // Force-directed settings
            quality: 'proof',
            randomize: true,

            nodeRepulsion: 80000,
            idealEdgeLength: 350,
            edgeElasticity: 0.1,
            nestingFactor: 0.1,

            // Gravity — keep centered, attract disconnected components
            gravity: 0.01,
            gravityRange: 8.0,
            gravityCompound: 0.3,
            gravityRangeCompound: 3.0,

            // Iterations
            numIter: 5000,

            // Cooling
            initialEnergyOnIncremental: 0.3,

            // Spacing
            nodeSeparation: 250,

            // Tiling for disconnected components
            tile: true,
            tilingPaddingVertical: 80,
            tilingPaddingHorizontal: 80,

            // Callback after layout completes - ensure graph fits in viewport
            stop: () => {
              setTimeout(() => {
                fitGraph(cy, 100)
              }, 50)
            },
          }
          cy.layout(fcoseLayout as cytoscape.LayoutOptions).run()
        }
      }

      // Resize observer to handle container size changes (e.g., sidebar toggle).
      //
      // Two guards make this stable when the canvas is in a transitioning
      // layout (sidebar mount animation, row expansions, etc.):
      //   1. We only refit when the size shifts by more than 4px. Sub-pixel
      //      reflows triggered by selection-driven style changes used to
      //      retrigger fit() and shrink the rendered area, leaving empty
      //      space below the graph.
      //   2. cy.resize() / cy.fit() run on an rAF tick so we read the
      //      container at its post-layout size, not at an intermediate frame
      //      of whatever animation is in flight.
      let resizeRaf: number | null = null
      let lastFitW = container.clientWidth
      let lastFitH = container.clientHeight
      const resizeObserver = new ResizeObserver(() => {
        if (resizeRaf !== null) cancelAnimationFrame(resizeRaf)
        resizeRaf = requestAnimationFrame(() => {
          resizeRaf = null
          cy.resize()
          const w = container.clientWidth
          const h = container.clientHeight
          if (Math.abs(w - lastFitW) > 4 || Math.abs(h - lastFitH) > 4) {
            lastFitW = w
            lastFitH = h
            fitGraph(cy, 50)
          }
        })
      })
      resizeObserver.observe(container)

      return () => {
        if (savePositionsTimeout) {
          clearTimeout(savePositionsTimeout)
        }
        if (resizeRaf !== null) cancelAnimationFrame(resizeRaf)
        resizeObserver.disconnect()
        authedImagesRef.current.release()
        authedImagesRef.current = new AuthedImageCache()
        cy.destroy()
        cyRef.current = null
        setIsReady(false)
      }
    }

    initGraph()
  }, [container, caseId]) // Reinitialize when container or caseId changes

  // Re-apply stylesheet when theme changes
  useEffect(() => {
    const cy = cyRef.current
    if (!cy || !isReady) return

    cy.style().fromJson(createSpectreStylesheet(theme)).update()
  }, [theme, isReady])

  // Apply the currently-selected layout. Exposed as `runLayout` so a toolbar
  // button can re-run it on demand (after drags have made a mess).
  const applyLayout = useCallback(() => {
    const cy = cyRef.current
    if (!cy || !isReady) return

    const layout = layoutRef.current
    if (layout === 'custom') {
      // Custom layout: keep nodes exactly where they are (saved positions or user-placed)
      // No automatic layout - user controls positioning
      return
    }

    if (layout === 'timeline') {
      // Timeline layout: arrange Event nodes chronologically, keep others visible
      const eventNodes = cy.nodes('[typeLabel = "Event"]')

      if (eventNodes.length > 0) {
        // Extract dates from Event nodes
        const nodesWithDates = eventNodes
          .map(node => {
            const props = node.data('properties') || {}
            const dateStr = props.date || props.created_at || props.timestamp || props.event_date
            const date = dateStr ? new Date(dateStr) : null
            return { node, date, dateStr }
          })
          .sort((a, b) => {
            if (!a.date || !b.date) return 0
            return a.date.getTime() - b.date.getTime()
          })

        // Calculate timeline positions
        const graphWidth = cy.width()
        const timelineY = 200 // Fixed Y position for timeline
        const spacing = Math.max(200, graphWidth / (nodesWithDates.length || 1))

        // Animate Event nodes to timeline
        safeBatch(cy, () => {
          nodesWithDates.forEach((item, index) => {
            item.node.animate({
              position: {
                x: 100 + index * spacing,
                y: timelineY,
              },
              duration: 500,
            })
          })
        })

        // Don't call cy.fit() - keep the current viewport
        // Don't re-layout other nodes - they stay exactly where they are
      }
    } else {
      // Run the appropriate layout based on layout type
      let layoutConfig: cytoscape.LayoutOptions

      switch (layout) {
        case 'grid':
          layoutConfig = {
            name: 'grid',
            animate: true,
            animationDuration: 500,
            fit: true,
            padding: fitPadding(50),
            avoidOverlap: true,
            avoidOverlapPadding: 10,
          }
          break

        case 'circle':
          layoutConfig = {
            name: 'circle',
            animate: true,
            animationDuration: 500,
            fit: true,
            padding: fitPadding(50),
            avoidOverlap: true,
          }
          break

        case 'breadthfirst':
          layoutConfig = {
            name: 'breadthfirst',
            animate: true,
            animationDuration: 500,
            fit: true,
            padding: fitPadding(50),
            directed: false,
            spacingFactor: 1.5,
            avoidOverlap: true,
          } as any
          break

        case 'concentric':
          layoutConfig = {
            name: 'concentric',
            animate: true,
            animationDuration: 500,
            fit: true,
            padding: fitPadding(50),
            avoidOverlap: true,
            concentric: (node: any) => node.degree(),
            levelWidth: () => 2,
          } as any
          break

        case 'clusters':
          layoutConfig = {
            name: 'fcose',
            animate: true,
            animationDuration: 1200,
            fit: true,
            padding: fitPadding(160), // room for compound labels on top
            quality: 'proof',
            randomize: true,

            // Intra-cluster: pull children of the same compound toward parent center
            nestingFactor: 0.3,
            gravityCompound: 1.5,
            gravityRangeCompound: 1.0,

            // Inter-cluster: push compound parents apart
            nodeRepulsion: 180000,
            idealEdgeLength: (edge: any) => {
              // Shorter intra-compound edges, longer inter-compound edges
              const src = edge.source()
              const tgt = edge.target()
              const sp = src.parent()
              const tp = tgt.parent()
              const sameCluster = sp.length && tp.length && sp.id() === tp.id()
              return sameCluster ? 120 : 500
            },
            edgeElasticity: 0.1,

            // Global
            gravity: 0.02,
            gravityRange: 4.0,
            numIter: 6000,
            nodeSeparation: 250,

            // Disconnected communities tile into a grid of bounding boxes
            tile: true,
            tilingPaddingVertical: 160,
            tilingPaddingHorizontal: 160,

            stop: () => {
              // Post-layout safety net: if compound bounding boxes overlap,
              // explicitly translate each compound's children so compound
              // centroids land on a coarse grid with guaranteed spacing.
              const cy = cyRef.current
              if (!cy) return
              expandCompoundCentroids(cy)
              setTimeout(() => fitGraph(cy, 120), 50)
            },
          } as any
          break

        case 'cola':
          layoutConfig = {
            name: 'cola',
            animate: true,
            animationDuration: 300,
            fit: true,
            padding: fitPadding(80),
            nodeSpacing: 80,
            edgeLength: (edge: any) => {
              // Weight edges by type for edge-weighted spring embedding
              const label = edge.data('label') || ''
              if (['FROM_CHUNK', 'NEXT_CHUNK', 'MENTIONED_IN', 'FROM_DOCUMENT'].includes(label)) {
                return 350
              }
              return 200
            },
            avoidOverlap: true,
            convergenceThreshold: 0.01,
            maxSimulationTime: 2000,
            ungrabifyWhileSimulating: false,
            flow: undefined,
            alignment: undefined,
          } as any
          break
        default: {
          layoutConfig = {
            name: 'fcose',
            animate: true,
            animationDuration: 1200,
            fit: true,
            padding: fitPadding(120),
            quality: 'proof',
            randomize: false,
            nodeRepulsion: 80000,
            idealEdgeLength: (edge: any) => {
              const label = edge.data('label') || ''
              if (['FROM_CHUNK', 'NEXT_CHUNK', 'MENTIONED_IN', 'FROM_DOCUMENT'].includes(label)) {
                return 400
              }
              return 350
            },
            edgeElasticity: 0.1,
            nestingFactor: 0.1,
            gravity: 0.01,
            gravityRange: 8.0,
            numIter: 5000,
            nodeSeparation: 250,
          } as any
          break
        }
      }

      const layoutRun = cy.layout(layoutConfig)
      // Persist new positions once the algorithm settles (covers both the
      // layout-dropdown change and the "Tidy up" toolbar button).
      layoutRun.one('layoutstop', () => {
        savePositionsRef.current()
        // The layout's own fit is symmetric; keep the result clear of the phone toolbar.
        if (isMobileViewport()) fitGraph(cy, 50)
      })
      layoutRun.run()
    }
  }, [isReady])

  // Re-apply layout only when the user explicitly changes the layout prop —
  // NOT on mount, and NOT when seeded from saved ui_state. Saved positions
  // (loaded into the preset layout at init) must survive remounts; running a
  // layout algorithm here would scramble them. The "Tidy up" toolbar button
  // is the on-demand trigger.
  //
  // The first run after isReady is treated as a baseline sync — we record
  // whatever `layout` value is current (initial default OR seeded from
  // ui_state) without running the algorithm. Subsequent prop changes are
  // real user actions and do run it.
  const prevLayoutRef = useRef(layout)
  const layoutBaselineRef = useRef(false)
  useEffect(() => {
    if (!isReady) return
    if (!layoutBaselineRef.current) {
      layoutBaselineRef.current = true
      prevLayoutRef.current = layout
      return
    }
    if (prevLayoutRef.current === layout) return
    prevLayoutRef.current = layout
    applyLayout()
  }, [layout, isReady, applyLayout])

  // Update data when it changes
  const updateData = useCallback((newData: GraphData) => {
    const cy = cyRef.current
    if (!cy) return

    const newElements = graphDataToCytoscapeElements(newData)

    safeBatch(cy, () => {
      // Get existing node/edge IDs
      const existingNodeIds = new Set(cy.nodes().map((n: NodeSingular) => n.id()))
      const existingEdgeIds = new Set(cy.edges().map((e: EdgeSingular) => e.id()))

      // Get new node/edge IDs
      const newNodeIds = new Set(newData.nodes.map(n => n.id))
      const newEdgeIds = new Set(newData.edges.map(e => e.id))

      // Remove nodes/edges that are no longer in data. Before removing a
      // compound parent, orphan its surviving children so they don't get
      // cascade-removed (Cytoscape removes all descendants of a compound).
      cy.nodes().forEach((node: NodeSingular) => {
        if (newNodeIds.has(node.id())) return
        if (node.isParent()) {
          node.children().forEach((child: NodeSingular) => {
            if (newNodeIds.has(child.id())) {
              child.move({ parent: null })
            }
          })
        }
        node.remove()
      })
      cy.edges().forEach((edge: EdgeSingular) => {
        if (!newEdgeIds.has(edge.id())) {
          edge.remove()
        }
      })

      // Add new nodes - IMPORTANT: Add compound nodes first, then children
      const nodesToAdd = newElements.filter(
        el => el.group === 'nodes' && !existingNodeIds.has(el.data.id)
      )
      if (nodesToAdd.length > 0) {
        // Separate compound nodes from regular nodes
        const compoundNodes = nodesToAdd.filter(
          el => el.group === 'nodes' && el.data.isCompound === true
        )
        const regularNodes = nodesToAdd.filter(el => el.group === 'nodes' && !el.data.isCompound)

        // Add compounds first, then regular nodes (Cytoscape requirement)
        if (compoundNodes.length > 0) {
          cy.add(compoundNodes)
        }
        if (regularNodes.length > 0) {
          cy.add(regularNodes)
        }
      }

      // Add new edges (addEdgesSafely drops any whose endpoint node is absent)
      const edgesToAdd = newElements
        .filter(el => el.group === 'edges')
        .filter(el => !existingEdgeIds.has(el.data.id))
      if (edgesToAdd.length > 0) {
        addEdgesSafely(cy, edgesToAdd)
        // Force style update to ensure new edges render correctly
        cy.style().update()
      }

      // Update existing node data
      newElements
        .filter(el => el.group === 'nodes' && existingNodeIds.has(el.data.id))
        .forEach(el => {
          // Type guard: el is a node element
          if (el.group !== 'nodes') return

          const node = cy.$id(el.data.id)
          if (node.length === 0) return

          const oldParent = node.data('parent')
          const newParent = el.data.parent

          if (oldParent !== newParent) {
            if (!newParent) {
              node.move({ parent: null })
            } else if (cy.$id(newParent).length === 0) {
              console.error(`[Cytoscape] Parent ${newParent} does not exist! Cannot move node.`)
              return
            } else {
              node.move({ parent: newParent })
            }
          }
          node.data(el.data)
        })
    })

    // Newly added (and re-synced) nodes may carry unresolved crops.
    void resolveAuthedNodeImages(cy, authedImagesRef.current)

    // Place new nodes near a connected existing neighbor instead of running
    // an isolated cose layout (which spreads them anywhere with no anchor).
    try {
      const nodesWithoutPosition = cy.nodes().filter((n: NodeSingular) => {
        if (n.isParent?.()) return false
        const pos = n.position()
        return pos.x === 0 && pos.y === 0
      })

      if (nodesWithoutPosition.length > 0) {
        const newIdSet = new Set(nodesWithoutPosition.map(n => n.id()))
        // Cluster new siblings sharing the same anchor so we can fan them out.
        const byAnchor = new Map<string, NodeSingular[]>()
        const orphans: NodeSingular[] = []

        nodesWithoutPosition.forEach((node: NodeSingular) => {
          // Find an already-placed neighbor (not in the new set).
          const anchor = node
            .connectedEdges()
            .connectedNodes()
            .filter(
              (n: NodeSingular) =>
                !newIdSet.has(n.id()) && !(n.position().x === 0 && n.position().y === 0)
            )
            .first()
          if (anchor.nonempty()) {
            const id = anchor.id()
            const arr = byAnchor.get(id) ?? []
            arr.push(node)
            byAnchor.set(id, arr)
          } else {
            orphans.push(node)
          }
        })

        const RADIUS = FAN_RADIUS
        safeBatch(cy, () => {
          byAnchor.forEach((children, anchorId) => {
            const anchor = cy.getElementById(anchorId)
            // Fan children around the anchor at evenly-spaced angles, with a
            // small random jitter so repeated expansions don't stack. The
            // passages of a recording are set beside it as a timeline instead:
            // dozens of chips do not fit a ring (expand-placement.ts).
            const baseAngle = Math.random() * Math.PI * 2
            placeBesideAnchor(anchor, children, newIdSet, baseAngle)
          })

          // Orphans (no edges to existing nodes yet) — drop near viewport
          // center so they aren't lost off-screen.
          if (orphans.length > 0) {
            const center = { x: cy.width() / 2, y: cy.height() / 2 }
            const viewportCenter = {
              x: (center.x - cy.pan().x) / cy.zoom(),
              y: (center.y - cy.pan().y) / cy.zoom(),
            }
            orphans.forEach((node, i) => {
              const angle = (i * Math.PI * 2) / Math.max(orphans.length, 6)
              node.position({
                x: viewportCenter.x + Math.cos(angle) * RADIUS,
                y: viewportCenter.y + Math.sin(angle) * RADIUS,
              })
            })
          }
        })
      }
    } catch (err) {
      console.warn('[Cytoscape] Layout error:', err)
    }
  }, [])

  // Navigation methods
  const fitToView = useCallback(() => {
    if (cyRef.current) fitGraph(cyRef.current, 50)
  }, [])

  const resize = useCallback(() => {
    cyRef.current?.resize()
  }, [])

  const zoomIn = useCallback(() => {
    const cy = cyRef.current
    if (!cy) return
    cy.zoom({
      level: cy.zoom() * 1.2,
      renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
    })
  }, [])

  const zoomOut = useCallback(() => {
    const cy = cyRef.current
    if (!cy) return
    cy.zoom({
      level: cy.zoom() * 0.8,
      renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
    })
  }, [])

  const resetZoom = useCallback(() => {
    cyRef.current?.zoom(1)
    cyRef.current?.center()
  }, [])

  const panToNode = useCallback((nodeId: string) => {
    const cy = cyRef.current
    if (!cy) return

    const node = cy.$id(nodeId)
    if (node.length > 0) {
      cy.animate({
        center: { eles: node },
        zoom: Math.max(cy.zoom(), 1),
        duration: 300,
      })
    }
  }, [])

  // Lasso selection state
  const [isLassoEnabled, setIsLassoEnabledState] = useState(false)

  const setLassoEnabled = useCallback((enabled: boolean) => {
    const cy = cyRef.current
    if (!cy) return

    // Use the cytoscape-lasso plugin API
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cyAny = cy as any
    if (typeof cyAny.lassoSelectionEnabled === 'function') {
      cyAny.lassoSelectionEnabled(enabled)
      // When lasso is enabled, disable panning so drawing works without modifier keys
      // When lasso is disabled, re-enable panning
      cy.userPanningEnabled(!enabled)
      setIsLassoEnabledState(enabled)
    }
  }, [])

  return {
    cy: cyRef.current,
    isReady,
    updateData,
    fitToView,
    resize,
    zoomIn,
    zoomOut,
    resetZoom,
    panToNode,
    setLassoEnabled,
    isLassoEnabled,
    runLayout: applyLayout,
    persist: () => savePositionsRef.current(),
  }
}
